import { useEffect, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import type { AdminMessage, MessageFeedbackInput } from '../../api/types.js';
import { FEEDBACK_TAGS, hasMessageFeedback } from '../../lib/utils.js';
import type { MessageFeedback } from '../../api/types.js';

interface FeedbackPopoverProps {
  message: AdminMessage;
  feedback: MessageFeedback | null;
  onSave: (messageId: number, input: MessageFeedbackInput) => Promise<void>;
}

export function FeedbackPopover({ message, feedback, onSave }: FeedbackPopoverProps) {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState<MessageFeedbackInput['rating']>(feedback?.rating ?? null);
  const [tags, setTags] = useState<string[]>(feedback?.tags ?? []);
  const [comment, setComment] = useState(feedback?.comment ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRating(feedback?.rating ?? null);
    setTags(feedback?.tags ?? []);
    setComment(feedback?.comment ?? '');
  }, [feedback, open]);

  const toggleTag = (tag: string) => {
    setTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(Number(message.id), {
        rating,
        tags,
        comment: comment.trim() || null,
      });
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const hasFeedback = hasMessageFeedback(feedback);
  const label = hasFeedback ? '已反馈' : '反馈';

  const form = (
    <>
      <h3 className="feedback-title">消息反馈</h3>
      <div className="feedback-rating" role="group" aria-label="反馈评分">
        <button
          className={`feedback-rating-btn ${rating === 'up' ? 'active' : ''}`}
          type="button"
          onClick={() => setRating('up')}
        >
          赞
        </button>
        <button
          className={`feedback-rating-btn ${rating === 'down' ? 'active' : ''}`}
          type="button"
          onClick={() => setRating('down')}
        >
          踩
        </button>
        <button
          className={`feedback-rating-btn clear ${rating === null || rating === undefined ? 'active' : ''}`}
          type="button"
          onClick={() => setRating(null)}
        >
          清除
        </button>
      </div>
      <div className="feedback-tags">
        {FEEDBACK_TAGS.map((tag) => (
          <button
            key={tag}
            className={`feedback-tag ${tags.includes(tag) ? 'active' : ''}`}
            type="button"
            onClick={() => toggleTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>
      <textarea
        className="feedback-comment"
        maxLength={2000}
        placeholder={
          message.direction === 'out'
            ? '这条回复有什么问题？可以写一条更好的回复...'
            : '用户对什么敏感？备注给 AI 优化...'
        }
        value={comment}
        onChange={(e) => setComment(e.target.value)}
      />
      <div className="feedback-actions">
        <button className="button ghost" type="button" onClick={() => setOpen(false)} disabled={saving}>
          取消
        </button>
        <button className="button primary" type="button" onClick={handleSave} disabled={saving}>
          {saving ? <span className="spinner" /> : '保存'}
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop popover */}
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            className={`feedback-trigger ${hasFeedback ? 'has-feedback' : ''}`}
            type="button"
            aria-label={label}
            title={label}
          >
            {hasFeedback ? '✓ ' : '+ '}
            {label}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content className="feedback-popover-content" side="top" align="end" sideOffset={8}>
            {form}
            <Popover.Arrow className="feedback-popover-arrow" />
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
}
